<template>
  <div class="modal fade" :class="{ show: show, 'd-block': show }" v-if="show" data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Edit Profile</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <div class="row">
            <!-- Left Sidebar Navigation -->
            <div class="col-md-3">
              <div class="edit-sidebar">
                <div
                  v-for="(menuItem, index) in menuItems"
                  :key="index"
                  class="step-item"
                  :class="{
                    active: currentStep === index + 1,
                    saved: savedSteps.includes(index + 1)
                  }"
                  @click="changeStep(index + 1)"
                >
                  <div class="step-number">
                    <i v-if="savedSteps.includes(index + 1)" class="bi bi-check"></i>
                    <span v-else>{{ index + 1 }}</span>
                  </div>
                  <div class="step-content">
                    <div class="step-title">{{ menuItem.title }}</div>
                    <div class="step-subtitle">{{ menuItem.subtitle }}</div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Right Form Content -->
            <div class="col-md-9">
              <form @submit.prevent="handleSubmit">
                <!-- Dynamic Step Content -->
                <component
                  v-if="hasActiveStep"
                  :is="currentStepComponent"
                  :form-data="currentStepFormData"
                  :profile-data="profileData"
                  :fellowship-index="currentFellowshipIndex"
                  :server-errors="validationErrors"
                  @update="updateFormData"
                  @save="saveCurrentStep"
                  @pending-state-change="handlePendingStateChange"
                  :key="currentStep"
                />
                <div v-else class="text-muted py-4">Loading profile data...</div>
              </form>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="previousStep"
            v-if="currentStep > 1"
            :disabled="saving || hasPendingStepChanges"
          >
            <i class="bi bi-chevron-left me-2"></i>Previous
          </button>
          <button type="button" class="btn btn-secondary" @click="$emit('close')" :disabled="saving">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            @click="saveAndNext"
            v-if="currentStep < menuItems.length"
            :disabled="saving || hasPendingStepChanges"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <i v-else class="bi bi-save me-2"></i>{{ saving ? 'Saving...' : 'Save & Next' }}
          </button>
          <button
            type="button"
            class="btn btn-success"
            @click="saveProfile"
            v-if="currentStep === menuItems.length"
            :disabled="saving || hasPendingStepChanges"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <i v-else class="bi bi-save me-2"></i>{{ saving ? 'Saving...' : 'Save Profile' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FellowshipForm from './forms/FellowshipForm.vue'
import PersonalDetailsForm from './forms/PersonalDetailsForm.vue'
import EducationForm from './forms/EducationForm.vue'
import ExperiencesForm from './forms/ExperiencesForm.vue'
import GrantsForm from './forms/GrantsForm.vue'
import ProjectsForm from './forms/ProjectsForm.vue'
import PublicationsForm from './forms/PublicationsForm.vue'
import UserService from "@/services/users/UserService";
import { sortFellowshipsByCallYear } from '@/utils/fellowshipOrder'

export default {
  name: 'EditProfileModal',
  components: {
    FellowshipForm,
    PersonalDetailsForm,
    EducationForm,
    ExperiencesForm,
    GrantsForm,
    ProjectsForm,
    PublicationsForm
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    profileData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'save', 'stepSaved', 'reload-profile', 'error'],
  data() {
    return {
      saving: false,
      currentStep: 1,
      dataInitialized: false,
      hasPendingStepChanges: false,
      savedSteps: [], // Track which steps have been saved
      menuItems: [],
      // Server validation errors, keyed as Laravel sends them
      // (e.g. 'publications.0.date'), so forms can highlight the exact row.
      validationErrors: {},
      editForm: this.getInitialFormData()
    }
  },
  computed: {
    activeMenuItem() {
      return this.menuItems[this.currentStep - 1] || null
    },
    hasActiveStep() {
      return !!this.activeMenuItem
    },
    currentStepComponent() {
      return this.activeMenuItem?.component || 'FellowshipForm'
    },
    currentFellowshipIndex() {
      return this.activeMenuItem?.fellowshipIndex ?? null
    },
    currentStepFormData() {
      if (this.activeMenuItem?.component === 'FellowshipForm') {
        return this.editForm.fellowshipDetails[this.currentFellowshipIndex] || {
          ...this.editForm.fellowship,
          collaborators: [],
          participants: [],
          students: [],
          sites: [],
          phd_study: []
        }
      }

      return this.editForm
    }
  },
  watch: {
    profileData: {
      handler(newData, oldData) {
        if (newData && Object.keys(newData).length > 0) {
          this.initializeFormData(newData)
        }
      },
      deep: true,
      immediate: true
    },
    show: {
      handler(newValue) {
        if (newValue) {
          this.currentStep = 1
          this.hasPendingStepChanges = false

          // Always reinitialize when modal opens
          this.$nextTick(() => {
            if (this.profileData && Object.keys(this.profileData).length > 0) {
              this.initializeFormData(this.profileData)
            }
          })
        } else {
          // Reset initialization state when modal closes
          this.dataInitialized = false
          this.hasPendingStepChanges = false
          this.savedSteps = []
        }
      }
    }
  },
  methods: {
    handlePendingStateChange(isPending) {
      this.hasPendingStepChanges = Boolean(isPending)
    },

    canNavigateAway() {
      if (!this.hasPendingStepChanges) return true

      this.$emit('error', 'Please save or cancel the item you are editing before navigating away from this section.')
      return false
    },

    buildMenu() {
      this.menuItems = [];

      const list = Array.isArray(this.editForm.fellowshipDetails)
        ? this.editForm.fellowshipDetails
        : [];

      list.forEach((item, index) => {
        this.menuItems.push({
          title: `EDCTP Fellowship ${index + 1}`,
          subtitle: 'Fellowship Information',
          component: 'FellowshipForm',
          fellowshipIndex: index
        });
      });

      // Add fixed menus
      this.menuItems.push(
        { title: 'Personal Details', subtitle: 'Titles & Areas', component: 'PersonalDetailsForm' },
        { title: 'Professional Profile', subtitle: 'Professional Profile', component: 'EducationForm' },
        { title: 'Awards & Professional Activities', subtitle: 'Awards & Professional Activities', component: 'ExperiencesForm' },
        { title: 'Grants', subtitle: 'Exclude EDCTP Fellowship(s)', component: 'GrantsForm' },
        { title: 'Publications', subtitle: 'Publications', component: 'PublicationsForm' }
      );

      if (this.currentStep < 1 || this.currentStep > this.menuItems.length) {
        this.currentStep = 1
      }
    },

    getInitialFormData() {
      return {
        fellowshipDetails: [],
        fellowship: {
          fellow: '',
          gender: '',
          programme: '',
          call: '',
          callDate: '',
          title: '',
          startDate: '',
          projectStart: '',
          endDate: '',
          objectives: '',
          studyDesign: '',
          projectSummary: '',
          numberOfSubjects: null,
          status: '',
          resultsAndOutcomes: '',
          diseaseArea: '',
          projectCode: '',
          collaborators: [],
          participants: [],
          students: [],
          sites: [],
          phd_study: [],
          fellowship_id: null
        },
        personal: {
          title: '',
          fullName: '',
          email: '',
          phone: '',
          dateOfBirth: '',
          nationality: '',
          address: '',
          currentOrganisation: '',
          currentJobTitle: '',
          country: '',
          specialisations: [],
          workingGroups: [],
          biography: '',
          linkedinLink: '',
          twitterLink: '',
          facebookLink: ''
        },
        education: [],
        boardMemberships: [],
        professionalPositions: [],
        awards: [],
        otherExperiences: [],
        grants: [],
        projects: [],
        publications: []
      }
    },

    initializeFormData(data) {
      try {
        if (!data || Object.keys(data).length === 0) {
          console.warn('Modal - No data received for initialization')
          return
        }

        let actualData = data
        if (data.success && data.data) {
          actualData = data.data
        }

        // Reset form to initial state first
        this.editForm = this.getInitialFormData()

        // Helper functions
        const formatDate = (dateString) => {
          if (!dateString) return ''
          const date = new Date(dateString)
          return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0]
        }

        const parseJson = (field, defaultValue = []) => {
          if (!field) return defaultValue
          if (Array.isArray(field)) return field
          try {
            return typeof field === 'string' ? JSON.parse(field) : defaultValue
          } catch (e) {
            console.warn('Error parsing JSON field:', field, e)
            return defaultValue
          }
        }

        // Get user name from multiple sources
        const userName = actualData.user?.name || actualData.name || ''

        // ========================================
        //          FELLOWSHIP DETAILS (MULTIPLE)
        // ========================================
        let fellowshipArray = actualData.fellowship_details || [];

        // If backend returns a single object instead of an array → convert it
        if (fellowshipArray && typeof fellowshipArray === 'object' && !Array.isArray(fellowshipArray)) {
          fellowshipArray = [ fellowshipArray ];
        }

        // 1. Store full fellowship list

        // Newest call year first (Fellowship 1), oldest last
        fellowshipArray = sortFellowshipsByCallYear(fellowshipArray, f => f?.callDate);

        this.editForm.fellowshipDetails = fellowshipArray.map(f => ({
          fellowship_id: f.fellowship_id,
          fellow: userName,
          gender: f.gender || '',
          programme: f.programme || '',
          call: f.call || '',
          callDate: f.callDate || '',
          title: f.title || '',
          startDate: f.startDate || '',
          endDate: f.endDate || '',
          projectCode: f.projectCode || '',
          objectives: f.objectives || '',
          studyDesign: f.studyDesign || '',
          projectSummary: f.projectSummary || '',
          numberOfSubjects: f.numberOfSubjects || null,
          status: f.status || '',
          resultsAndOutcomes: f.resultsAndOutcomes || '',
          diseaseArea: f.diseaseArea || '',
          collaborators: parseJson(f.collaborators, []),
          participants: parseJson(f.participants, []),
          students: parseJson(f.students, []),
          sites: parseJson(f.sites, []),
          phd_study: parseJson(f.phdStudy, [])
        }));

        // ========================================
        //                PERSONAL DATA
        // ========================================
        const personalData = actualData.personal || {}
        this.editForm.personal = {
          title: personalData.title || '',
          fullName: personalData.fullName || userName,
          email: personalData.email || actualData.user?.email || '',
          phone: personalData.phone || '',
          dateOfBirth: formatDate(personalData.dateOfBirth),
          nationality: personalData.nationality || '',
          address: personalData.address || '',
          currentOrganisation: personalData.currentOrganisation || '',
          currentJobTitle: personalData.currentJobTitle || '',
          country: personalData.country || '',
          biography: personalData.biography || '',
          linkedinLink: personalData.linkedinLink || '',
          twitterLink: personalData.twitterLink || '',
          facebookLink: personalData.facebookLink || '',
          specialisations: parseJson(personalData.specialisations, []),
          workingGroups: parseJson(personalData.workingGroups, [])
        }

        // ========================================
        //       OTHER PROFILE SECTIONS
        // ========================================
        this.editForm.education = parseJson(actualData.education, [])
        this.editForm.boardMemberships = parseJson(actualData.boardMemberships, [])
        this.editForm.professionalPositions = parseJson(actualData.professionalPositions, [])
        this.editForm.awards = parseJson(actualData.awards, [])
        this.editForm.otherExperiences = parseJson(actualData.otherExperiences, [])
        this.editForm.grants = parseJson(actualData.grants, [])
        this.editForm.projects = parseJson(actualData.projects, [])
        this.editForm.publications = parseJson(actualData.publications, [])

        // ========================================
        //        BUILD MENU TABS (IMPORTANT)
        // ========================================
        this.buildMenu()

        // Mark as initialized and force component updates
        this.dataInitialized = Date.now()

        this.$nextTick(() => {
          this.$forceUpdate()
        })

      } catch (error) {
        console.error('Modal - Error initializing form data:', error)
        console.error('Modal - Error stack:', error.stack)
      }
    },

    changeStep(step) {
      if (step !== this.currentStep && !this.canNavigateAway()) {
        return
      }

      this.hasPendingStepChanges = false
      this.currentStep = step
    },

    async saveAndNext() {
      if (!this.canNavigateAway()) {
        return
      }

      this.saving = true

      try {
        // Save the current step's data
        await this.saveCurrentStep()

        // Give the parent component time to reload
        await new Promise(resolve => setTimeout(resolve, 500))

        // Move to next step - do this after successful save
        if (this.currentStep < this.menuItems.length) {
          this.currentStep++
        }

      } catch (error) {
        console.error('Error saving and moving to next step:', error)
        // Tell the user why we did not move on. Without this the button just
        // looks broken.
        this.$emit('error', error?.message || 'Failed to save data. Please try again.')

      } finally {
        this.saving = false
      }
    },

    previousStep() {
      if (!this.canNavigateAway()) {
        return
      }

      if (this.currentStep > 1) {
        this.hasPendingStepChanges = false
        this.currentStep--
      }
    },

    async saveCurrentStep() {
      try {
        this.clearValidationErrors()

        const stepData = this.transformFormDataForAPI(this.currentStep)
        const response = await UserService.updateProfile(stepData)

        // Handle the nested response structure from your API
        let actualResponse = response

        // Check if response has nested Data structure
        if (response && response.Data) {
          actualResponse = response.Data
        }

        // Now check for success in the correct location
        if (actualResponse && (
          actualResponse.success === true ||
          actualResponse.status === 'success' ||
          // Also check the outer Success field
          response.Success === true ||
          // Check for HTTP status codes
          response.status === 200 ||
          actualResponse.status === 200
        )) {
          // Mark this step as saved
          if (!this.savedSteps.includes(this.currentStep)) {
            this.savedSteps.push(this.currentStep)
          }

          this.$emit('stepSaved', {
            step: this.currentStep,
            stepName: this.menuItems[this.currentStep - 1].title,
            data: stepData
          })

          // Keep parent profile data in sync so reopening the modal uses fresh data.
          this.$emit('reload-profile')

          return true

        } else {
          // A 422 lands here. Surface the per-field errors instead of throwing
          // an opaque error the UI then swallows - otherwise the save silently
          // fails and the buttons just look broken.
          const validationErrors = response?.errors
            || response?._originalResponse?.Data?.errors
            || null

          if (validationErrors) {
            this.setValidationErrors(validationErrors)

            const error = new Error(this.describeValidationErrors(validationErrors))
            error.validationErrors = validationErrors
            throw error
          }

          console.error('Unexpected response structure:', response)
          throw new Error(response?.message || 'Failed to save. Please try again.')
        }

      } catch (error) {
        console.error('Error saving current step:', error)
        throw error
      }
    },

    /**
     * Laravel sends index-addressable keys such as "publications.0.date" or
     * "fellowship.students.2.startDate". Keep them as-is for the child forms
     * (which look up their own section) and remember them for display.
     */
    setValidationErrors(errors) {
      this.validationErrors = errors || {}
    },

    clearValidationErrors() {
      this.validationErrors = {}
    },

    /**
     * A short, human message naming the sections that failed, for the toast.
     */
    describeValidationErrors(errors) {
      const messages = Object.values(errors)
        .flat()
        .filter(Boolean)

      if (messages.length === 0) {
        return 'Some entries could not be saved. Please review the highlighted fields.'
      }

      const shown = messages.slice(0, 3).join(' ')

      return messages.length > 3
        ? `${shown} (and ${messages.length - 3} more)`
        : shown
    },

    // Better update handling with proper data merging
    updateFormData(stepData) {
      if (stepData.reloadProfile) {
        this.$emit('reload-profile')
        return
      }

      // Handle different form types properly
      if (stepData.fellowship) {
        const menuItem = this.menuItems[this.currentStep - 1] || {};
        const index = menuItem.fellowshipIndex;

        if (index !== null && index !== undefined && this.editForm.fellowshipDetails[index]) {
          // Only update THIS fellowship index
          this.editForm.fellowshipDetails.splice(index, 1, {
            ...this.editForm.fellowshipDetails[index],
            ...stepData.fellowship
          });
        }

        return;
      } else if (stepData.personal) {
        // PersonalDetailsForm update
        this.editForm.personal = { ...this.editForm.personal, ...stepData.personal }

      } else if (stepData.education !== undefined) {
        // EducationForm update - handle all three arrays
        this.editForm.education = Array.isArray(stepData.education) ? [...stepData.education] : []
        if (stepData.boardMemberships !== undefined) {
          this.editForm.boardMemberships = Array.isArray(stepData.boardMemberships) ? [...stepData.boardMemberships] : []
        }
        if (stepData.professionalPositions !== undefined) {
          this.editForm.professionalPositions = Array.isArray(stepData.professionalPositions) ? [...stepData.professionalPositions] : []
        }

      } else if (stepData.experiences) {
        // ExperiencesForm update
        // Students supervised lives only on the EDCTP Fellowship tab now.
        if (stepData.experiences.awards !== undefined) {
          this.editForm.awards = Array.isArray(stepData.experiences.awards) ? [...stepData.experiences.awards] : []
        }
        if (stepData.experiences.otherExperiences !== undefined) {
          this.editForm.otherExperiences = Array.isArray(stepData.experiences.otherExperiences) ? [...stepData.experiences.otherExperiences] : []
        }

      } else if (stepData.grants !== undefined) {
        // GrantsForm update
        this.editForm.grants = Array.isArray(stepData.grants) ? [...stepData.grants] : []

      } else if (stepData.projects !== undefined) {
        // ProjectsForm update
        this.editForm.projects = Array.isArray(stepData.projects) ? [...stepData.projects] : []

      } else if (stepData.publications !== undefined) {
        // PublicationsForm update
        this.editForm.publications = Array.isArray(stepData.publications) ? [...stepData.publications] : []

      } else {
        // Handle direct field updates (like awards, etc.)
        Object.keys(stepData).forEach(key => {
          if (Object.prototype.hasOwnProperty.call(this.editForm, key)) {
            if (Array.isArray(stepData[key])) {
              this.editForm[key] = [...stepData[key]]
            } else {
              this.editForm[key] = stepData[key]
            }
          }
        })
      }
    },

    handleSubmit() {
      // Handle form submission for the current step
      this.saveCurrentStep()
    },

    async saveProfile() {
      try {
        if (!this.canNavigateAway()) {
          return
        }

        this.saving = true

        // Save the current step first
        await this.saveCurrentStep()

        // Transform the form data to match the API structure.
        // Pass the current step: without it menuItem resolves to {} and the
        // payload falls back to fellowshipDetails[0], which for a fellow with
        // more than one fellowship submits the wrong record.
        const profileData = this.transformFormDataForAPI(this.currentStep)

        this.$emit('save', profileData)
      } catch (error) {
        console.error('Error preparing profile data:', error)
        // Surface it: this is the "Save Profile does not close the dialog" case,
        // where the modal stays open with no explanation.
        this.$emit('error', error?.message || 'Failed to save your profile. Please try again.')
      } finally {
        this.saving = false
      }
    },

    transformFormDataForAPI(currentStep) {
      const menuItem = this.menuItems[currentStep - 1] || {};

      // Normalize publications: authors MUST be arrays
      if (Array.isArray(this.editForm.publications)) {
        this.editForm.publications = this.editForm.publications.map(pub => ({
          ...pub,
          authors: Array.isArray(pub.authors)
            ? pub.authors
            : (pub.authors ? pub.authors.split(',').map(a => a.trim()) : [])
        }));
      }

      // No blank fallback: a user with zero fellowships must not send a fellowship payload
      let fellowshipSource = null;

      if (menuItem.component === 'FellowshipForm') {
        const idx = menuItem.fellowshipIndex;

        if (idx !== null && idx !== undefined) {
          fellowshipSource = this.editForm.fellowshipDetails[idx];
        }
      } else if (
        Array.isArray(this.editForm.fellowshipDetails) &&
        this.editForm.fellowshipDetails.length > 0
      ) {
        // Fallback: use the first fellowship in the list
        fellowshipSource = this.editForm.fellowshipDetails[0];
      }

      const hasFellowship = !!fellowshipSource;
      fellowshipSource = fellowshipSource || {};

      const payload = {
        // Fellowship data - ensure field names match backend expectations
        fellowship: {
          fellow: fellowshipSource.fellow || '',
          fellowship_id: fellowshipSource.fellowship_id || null,
          gender: fellowshipSource.gender || '',
          programme: fellowshipSource.programme || '',
          call: fellowshipSource.call || '',
          callDate: fellowshipSource.callDate || '',
          title: fellowshipSource.title || '',
          startDate: fellowshipSource.startDate || '',
          endDate: fellowshipSource.endDate || '',
          objectives: fellowshipSource.objectives || '',
          studyDesign: fellowshipSource.studyDesign || '',
          projectSummary: fellowshipSource.projectSummary || '',
          numberOfSubjects: fellowshipSource.numberOfSubjects || null,
          status: fellowshipSource.status || '',
          resultsAndOutcomes: fellowshipSource.resultsAndOutcomes || '',
          diseaseArea: fellowshipSource.diseaseArea || '',
          projectCode: fellowshipSource.projectCode || '',

          // Arrays
          collaborators: Array.isArray(fellowshipSource.collaborators) ? fellowshipSource.collaborators : [],
          participants: Array.isArray(fellowshipSource.participants) ? fellowshipSource.participants : [],
          students: Array.isArray(fellowshipSource.students) ? fellowshipSource.students : [],
          sites: Array.isArray(fellowshipSource.sites) ? fellowshipSource.sites : [],
          phd_study: Array.isArray(fellowshipSource.phd_study) ? fellowshipSource.phd_study : []
        },

        // Personal data (fellow_details table)
        personal: {
          ...this.editForm.personal,
          specialisations: Array.isArray(this.editForm.personal.specialisations)
            ? this.editForm.personal.specialisations
            : [],
          workingGroups: Array.isArray(this.editForm.personal.workingGroups)
            ? this.editForm.personal.workingGroups
            : []
        },

        // Root-level arrays
        education: Array.isArray(this.editForm.education) ? this.editForm.education : [],
        boardMemberships: Array.isArray(this.editForm.boardMemberships) ? this.editForm.boardMemberships : [],
        professionalPositions: Array.isArray(this.editForm.professionalPositions) ? this.editForm.professionalPositions : [],
        awards: Array.isArray(this.editForm.awards) ? this.editForm.awards : [],
        otherExperiences: Array.isArray(this.editForm.otherExperiences) ? this.editForm.otherExperiences : [],
        grants: Array.isArray(this.editForm.grants) ? this.editForm.grants : [],
        projects: Array.isArray(this.editForm.projects) ? this.editForm.projects : [],
        publications: Array.isArray(this.editForm.publications) ? this.editForm.publications : []
      }

      // Zero fellowships: omit the key so the backend does not create an empty record
      if (!hasFellowship) delete payload.fellowship

      return payload
    }
  },

  // Initialize data when component is mounted
  mounted() {
    if (this.show && this.profileData && Object.keys(this.profileData).length > 0) {
      this.$nextTick(() => {
        this.initializeFormData(this.profileData)
      })
    }
  }
}
</script>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
.modal-dialog {
  max-width: 1200px;
  height: calc(100vh - 3.5rem);
}
.modal-content {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.modal-body {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  padding: 0;
}
.modal-body .row {
  flex: 1 1 auto;
  margin: 0;
  flex-wrap: nowrap;
  min-height: 0;
  width: 100%;
}
.modal-body .col-md-3 {
  overflow-y: auto;
  padding: 1rem;
  border-right: 1px solid #dee2e6;
  flex-shrink: 0;
  min-height: 0;
}
.modal-body .col-md-9 {
  overflow-y: auto;
  padding: 1.5rem;
  min-height: 0;
}
.edit-sidebar {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  height: fit-content;
}
.step-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.step-item:hover {
  background-color: #e9ecef;
}
.step-item.active {
  background-color: #17a2b8;
  color: white;
}
.step-item.saved {
  border-left: 4px solid #28a745;
}
.step-item.active.saved {
  border-left: 4px solid white;
}
.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  margin-right: 1rem;
  flex-shrink: 0;
}
.step-item.active .step-number {
  background-color: white;
  color: #17a2b8;
}
.step-item.saved .step-number {
  background-color: #28a745;
  color: white;
}
.step-item.active.saved .step-number {
  background-color: white;
  color: #28a745;
}
.step-content {
  flex: 1;
}
.step-title {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  margin-bottom: 0.25rem;
}
.step-subtitle {
  font-size: var(--font-size-sm);
  opacity: 0.8;
}
.form-step {
  min-height: 400px;
}
.modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
@media (max-width: 768px) {
  .modal-dialog {
    max-width: 95%;
    margin: 1rem;
    height: calc(100vh - 2rem);
  }
  .modal-body .row {
    flex-wrap: wrap;
  }
  .edit-sidebar {
    margin-bottom: 1rem;
  }
  .step-item {
    padding: 0.75rem;
  }
  .step-number {
    width: 28px;
    height: 28px;
    font-size: var(--font-size-sm);
  }
}
</style>
